import { apiClient } from './api';

export interface SalesStats {
  total_sales_mtd: number;
  orders_mtd: number;
  sales_reps_count: number;
  avg_deal_size: number;
}

export interface SalesRepRow {
  rep_id: number;
  rep_name: string;
  revenue_mtd: number;
  margin_percent: number;
  customer_count: number;
  target_revenue: number;
  achievement_percent: number;
  commission: number;
}

export interface SalesLeaderboard {
  stats: SalesStats;
  reps: SalesRepRow[];
}

export interface AdminOverview {
  todays_sales: number;
  todays_orders: number;
  active_customers: number;
  pending_orders: number;
  open_tickets: number;
  outstanding_receivables: number;
}

export async function getSalesLeaderboard(): Promise<SalesLeaderboard> {
  return apiClient.get<SalesLeaderboard>('/sales/leaderboard');
}

export async function getAdminOverview(): Promise<AdminOverview> {
  return apiClient.get<AdminOverview>('/admin/overview');
}

export interface NeedsAttentionItem {
  type: string;
  label: string;
  detail: string;
}

export interface AdminDashboardComparison {
  previous_period_revenue: number;
  previous_period_margin_pct: number;
  previous_period_orders: number;
}

export interface AdminDashboardData {
  stats: {
    total_revenue: number;
    revenue_change_pct: number;
    gross_margin_pct: number;
    active_customers: number;
    customers_change_pct: number;
    orders_today: number;
    orders_today_change_pct: number;
    avg_order_value: number;
    aov_change_pct: number;
    pending_orders: number;
    open_tickets: number;
    outstanding_receivables: number;
  };
  revenue_trend: { day: string; revenue: number }[];
  top_customers: { name: string; revenue: number }[];
  best_selling: { rank: number; name: string; category: string; units_sold: number }[];
  order_status: { label: string; value: number }[];
  route_performance: { route: string; revenue: number }[];
  needs_attention: NeedsAttentionItem[];
  comparison?: AdminDashboardComparison;
}

export async function getAdminDashboard(options: { compare?: boolean } = {}): Promise<AdminDashboardData> {
  return apiClient.get<AdminDashboardData>('/admin/dashboard', options.compare ? { compare: 'previous_period' } : undefined);
}

export interface DashboardDrilldownRow {
  order_id: number;
  customer_name: string;
  created_at: string;
  total_amount: number;
  cost: number;
  margin_percent: number;
}

export async function getDashboardDrilldown(limit = 50): Promise<DashboardDrilldownRow[]> {
  const raw = await apiClient.get<any>('/admin/dashboard/drilldown', { limit });
  return Array.isArray(raw) ? raw : (raw?.data ?? []);
}
